import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Camera, CalendarCheck2, ReceiptText } from "lucide-react";

// Home-page pitch for the customer portal — between Reviews and the footer. Every visitor
// should see the portal as a reason to choose Solid Home Services; taps through to /portal.
const POINTS = [
  { Icon: Camera, text: "Photos and notes from every visit, saved to your page" },
  { Icon: CalendarCheck2, text: "Project status at a glance — no calling to ask" },
  { Icon: ReceiptText, text: "Invoices, hours, and receipts — pay right from the page" },
];

// Real screenshot of the sample portal (/portal/demo) — potential customers see the
// actual product, not a mockup. Asset: /public/portal-sample.png (retake if the portal UI changes).
const PortalPeek = () => (
  <Link
    to="/portal/demo"
    data-testid="portal-promo-screenshot-link"
    className="block rotate-1 hover:rotate-0 transition-transform duration-500 border border-border shadow-xl bg-card p-2 hover:border-terracotta/50"
  >
    <img
      src="/portal-sample.png"
      alt="Sample Solid Home Services customer portal — job history with hours, invoices, and visit photos"
      data-testid="portal-promo-screenshot"
      className="w-full h-auto"
      loading="lazy"
    />
    <p className="text-[10px] text-muted-foreground text-center py-2 font-mono uppercase tracking-wider">
      Sample portal — tap to explore it live
    </p>
  </Link>
);

export const PortalPromo = () => (
  <section id="portal" data-testid="portal-promo-section" className="bg-navy text-primary-foreground">
    <div className="px-4 sm:px-8 lg:px-16 mx-auto max-w-7xl py-16 sm:py-24 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
      >
        <p className="overline-label mb-4 !text-terracotta">Your own customer portal</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
          Every visit to your home, documented on your own private page.
        </h2>
        <ul className="mt-8 space-y-4">
          {POINTS.map((p) => (
            <li key={p.text} className="flex items-start gap-3">
              <p.Icon className="h-5 w-5 text-terracotta shrink-0 mt-0.5" />
              <span className="text-primary-foreground/85 text-sm sm:text-base leading-relaxed">{p.text}</span>
            </li>
          ))}
        </ul>
        <div className="mt-9">
          <Link
            to="/portal"
            data-testid="portal-promo-cta"
            className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white font-semibold px-7 h-14 text-base transition-colors"
          >
            See the customer portal <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-3 text-xs text-primary-foreground/60">Free with any job — no password, no app.</p>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay: 0.15 }}
      >
        <PortalPeek />
      </motion.div>
    </div>
  </section>
);
